import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowsLeftRightIcon } from "@phosphor-icons/react";

/*
  "We found your previous account — import your old data?"

  Shown after a Microsoft sign-in under a new @indorama.com address when an
  old @ng / @sn.indorama.com account exists under the same name (see the note
  above AuthProvider in AuthContext.jsx). The person decides; nothing is
  merged without their say-so.

  It can only be answered with one of the two buttons — no Esc, no click-away
  — since closing it would otherwise look like a third, undefined choice.
*/
export default function ImportAccountDialog() {
  const { user, importOffer, importLegacyAccount, declineLegacyImport } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!importOffer || !user) return null;

  const onImport = async () => {
    setBusy(true);
    try {
      await importLegacyAccount();
      // On success the browser is already redirecting to sign in again.
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="rounded-md max-w-md [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="import-account-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ArrowsLeftRightIcon size={20} weight="bold" color="#0176D3" /> We found your previous account
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-[#334155]">
          <p>
            You've signed in as <span className="font-mono text-[#0F172A]">{user.email}</span>. There's an
            existing account for <span className="font-mono text-[#0F172A]">{importOffer.legacyEmail}</span>.
          </p>
          <p>Would you like to import your old data — projects, tasks, leave and your role?</p>
          <ul className="text-xs text-[#475569] list-disc pl-5 space-y-1">
            <li><strong>Import</strong>: your old account moves to {user.email} and keeps everything. You'll be signed in again automatically.</li>
            <li><strong>Start fresh</strong>: keep this new, empty account. The old one stays as it is.</li>
          </ul>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={declineLegacyImport} disabled={busy} data-testid="import-account-decline">
            Start fresh
          </Button>
          <Button onClick={onImport} disabled={busy} data-testid="import-account-confirm"
                  className="bg-[#0176D3] hover:bg-[#005FB2] text-white">
            {busy ? "Importing…" : "Import my old data"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
